import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Users, Key, Settings, BarChart2, Compass, Network, FolderTree, Target, Calendar, Edit2, FileText, TrendingUp, Activity } from '../icons';
import { SidebarLayout } from './SidebarLayout';
import { RoleSwitcher } from '../RoleSwitcher';
import { useAuth } from '../../contexts/AuthContext';
import { isUserCoach } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../LoadingSpinner';
import type { SidebarItem } from './Sidebar';

export function CoachLayout() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasAIAccess, setHasAIAccess] = useState(false);
  const [hasTeamsAccess, setHasTeamsAccess] = useState(false);
  const [hasCRMAccess, setHasCRMAccess] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    const init = async () => {
      const isCoach = await isUserCoach(user.id);
      if (!isCoach) {
        navigate('/');
        return;
      }

      const { data: coachData } = await supabase
        .from('coaches')
        .select('tier, teams_access_enabled, crm_enabled')
        .eq('user_id', user.id)
        .maybeSingle();

      setHasAIAccess(coachData?.tier === 'advanced' || coachData?.tier === 'partner');
      setHasTeamsAccess(coachData?.teams_access_enabled === true);
      setHasCRMAccess(coachData?.crm_enabled === true);
      setAuthorized(true);
      setChecking(false);
    };

    init();
  }, [user, authLoading, navigate]);

  if (authLoading || checking || !authorized) {
    return <LoadingSpinner message="Loading..." />;
  }

  const items: SidebarItem[] = [
    // Profile
    { group: 'Profile', label: 'Dashboard', path: '/coach', icon: Brain },
    { group: 'Profile', label: 'Respondents', path: '/coach/respondents', icon: Users },
    { group: 'Profile', label: 'Assessment Codes', path: '/coach/codes', icon: Key },
    { group: 'Profile', label: 'Projects', path: '/coach/projects', icon: FolderTree, gated: !hasTeamsAccess, gateMessage: 'Contact your partner to enable' },
    { group: 'Profile', label: 'Teams', path: '/coach/teams', icon: Network, gated: !hasTeamsAccess, gateMessage: 'Contact your partner to enable' },

    // Pattern
    { group: 'Pattern', label: 'AI Analysis', path: '/coach/manual-analysis', icon: Brain, gated: !hasAIAccess, gateMessage: 'Requires Advanced tier' },

    // Progress
    { group: 'Progress', label: 'Client Progress', path: '/coach/progress', icon: Activity },
    { group: 'Progress', label: 'Transformation Reports', path: '/coach/transformation-reports', icon: FileText },

    // Pathways
    { group: 'Pathways', label: 'Transformation Pathways', path: '/coach/pathways', icon: Compass, gated: !hasAIAccess, gateMessage: 'Requires Advanced tier' },
    { group: 'Pathways', label: 'Habit Installation OS\u2122', path: '/coach/habit-os', icon: Target },

    // Presence
    { group: 'Presence', label: 'Coaching Sessions', path: '/coach/sessions', icon: Calendar, gated: !hasCRMAccess, gateMessage: 'Contact your partner to enable' },
    { group: 'Presence', label: 'Session Intelligence\u2122', path: '/coach/session-intelligence', icon: Brain, gated: !hasCRMAccess, gateMessage: 'Contact your partner to enable' },

    // Proof
    { group: 'Proof', label: 'Team Reports', path: '/coach/reports', icon: BarChart2, gated: !hasTeamsAccess, gateMessage: 'Contact your partner to enable' },
    { group: 'Proof', label: 'Individual Reports', path: '/coach/respondents', icon: FileText },

    // Promote
    { group: 'Promote', label: 'Lead Page', path: '/coach/lead-page', icon: Edit2, gated: !hasCRMAccess, gateMessage: 'Contact your partner to enable' },
    { group: 'Promote', label: 'Sales Pipeline', path: '/coach/crm', icon: Target, gated: !hasCRMAccess, gateMessage: 'Contact your partner to enable' },

    // Profit
    { group: 'Profit', label: 'Profit Center', path: '/coach/profit', icon: TrendingUp },

    // Settings
    { group: 'Settings', label: 'AI Coach Settings', path: '/coach/ai-settings', icon: Settings, gated: !hasAIAccess, gateMessage: 'Requires Advanced tier' },
    { group: 'Settings', label: 'AI Coach Usage', path: '/coach/ai-usage', icon: BarChart2, gated: !hasAIAccess, gateMessage: 'Requires Advanced tier' },
  ];

  return (
    <SidebarLayout
      items={items}
      title="Coach"
      subtitle="Manage respondents & insights"
      headerSlot={<RoleSwitcher currentRole="coach" />}
    />
  );
}
